import { ReportSection, ReportSectionItemContent } from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import type { LedgerBudgetAndExecutionSectionProps } from '@/features/mold/models/ledger';
import {
  convertBudgetAndExecutionValue,
  getBudgetAndExecutionTableData,
} from '@/features/project/_utils/budget-and-execution';
import { useLocaledAmount } from '@/providers';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_RIGHT_ALIGNED_STYLE,
  type TableRowType,
  getEmptyTableDataByHeader,
} from '../../../../utils';
import { TableContent } from '../TableContent';
import { BudgetBreakdownTable } from './BudgetBreakdownTable';

export const BudgetAndExecutionSection: FC<
  LedgerBudgetAndExecutionSectionProps
> = ({
  project,
  consecutiveFiscalYears,
  consecutiveRequestAmounts,
  consecutiveBudgets,
  consecutiveExecutions,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();

  // NOTE: Note that the display conditions are different from those of the projectDetailsPage
  const fiscalYear = project?.fiscalYear;
  const budgetBreakdownTableData = useMemo<TableRowType>(() => {
    if (!fiscalYear) {
      return [];
    }
    const {
      tableDataSource,
      tableYears: budgetAndExecutionTableTableColumnKeys,
    } = getBudgetAndExecutionTableData({
      consecutiveFiscalYears,
      consecutiveRequestAmounts,
      consecutiveBudgets,
      consecutiveExecutions,
    });

    // Show "last-3-years + project-fiscal-year + next-fiscal-year".
    const tableColumnKeys = budgetAndExecutionTableTableColumnKeys.reduce<
      number[]
    >((arr, cur) => {
      const isNextYear = cur === fiscalYear + 1;
      const isCurrentYear = cur === fiscalYear;
      const isLast3Year = cur >= fiscalYear - 3 && cur < fiscalYear;
      if (isLast3Year || isNextYear || isCurrentYear) {
        arr.push(cur);
      }
      return arr;
    }, []);

    const WIDTH = 200;
    const firstCellStyle = { minWidth: WIDTH, maxWidth: WIDTH };
    const header: TableRowType[number] = {
      isHeader: true,
      row: [
        {
          isSpace: true,
          cellStyle: firstCellStyle,
        },
        ...tableColumnKeys.map((year) => ({
          text: year,
          textWrapperStyle: { justifyContent: 'center' },
        })),
      ],
    };

    const data = tableDataSource.flatMap<TableRowType[number]>((data) => {
      if (data.hideInPdf) {
        return [];
      }
      return {
        row: [
          {
            text: data.name,
            cellStyle: firstCellStyle,
          },
          ...tableColumnKeys.flatMap((year) => {
            const target = data.valueByYear?.find((v) => v.year === year);
            if (!target) {
              return [];
            }
            return [
              {
                textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
                text: convertBudgetAndExecutionValue({
                  value: target.value,
                  name: data.name,
                  unit,
                  hasNegativeValue: target.hasNegativeValue,
                  isDebugMode: devtoolState?.isDebugMode,
                }),
              },
            ];
          }),
        ],
      };
    });
    return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
  }, [
    consecutiveBudgets,
    consecutiveExecutions,
    consecutiveFiscalYears,
    consecutiveRequestAmounts,
    fiscalYear,
    unit,
    devtoolState?.isDebugMode,
  ]);

  return (
    <ReportSection
      title={t('budget-and-execution')}
      items={[
        {
          subtitle: `${t('budget-execution-table')}\n${t('report-amount-unit-description')}`,
          item: <TableContent noneBorder data={budgetBreakdownTableData} />,
        },
        {
          subtitle: `${t('budget-breakdown-table')}\n${t('report-amount-unit-description')}`,
          item: <BudgetBreakdownTable project={project} />,
        },
        {
          subtitle: t('increase-decrease-main-reason'),
          innerStyle: { display: 'flex' },
          item: (
            <>
              <ReportSectionItemContent
                wrapperStyle={{ flex: 0.965 }}
                value={project?.increaseDecreaseMainReason}
                isLast
              />
              <ReportSectionItemContent
                label={t('other-special-notes')}
                value={project?.otherSpecialNotes}
                hasLeftBorder
                isLast
              />
            </>
          ),
        },
      ]}
    />
  );
};
